import { RestError } from "../../services/rest-service/RestService";
import SaveIcon from '@mui/icons-material/Save';
import { Box, Fab, useTheme } from "@mui/material";
import React, { Ref, forwardRef, useImperativeHandle, useRef, useState } from "react";
import { isMobile, isTablet } from 'react-device-detect';
import { useTranslation } from "react-i18next";
import { ITagValue } from "../../common/models/ITagValue";
import { BaseActionEnum } from "../../components/action-menu/ActionMenuItem";
import { ContentView } from "../../components/content-view/ContentView";
import ContextualAppBar from "../../components/contextual-app-bar/ContextualAppBar";
import { ViewOptionStateEnum } from "../../components/contextual-app-bar/ViewOptionStateEnum";
import ObjectTagValues, { ObjectTagValuesType } from "../../components/object-tag-values/ObjectTagValues";
import { ObjectTagValuesMode } from '../../components/object-tag-values/ObjectTagValuesMode';
import UnsavedChangesPrompt from "../../components/unsaved-changes-prompt/UnsavedChangesPrompt";
import { useAppSettings } from "../../contexts/app-context/AppContext";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import TagValueDataService from "../../services/tag-value-data-service/TagValueDataService";



export type ObjectManualEntryViewProps = {

    hideAppBar?: boolean;
    hideOptions?: boolean;
    date?: Date;

    // visible?: boolean;
    // mode: ObjectTagValuesMode;
    // grouped: boolean;
    // onValueChanged?: (tagValue: ITagValue) => void;
    // autoHeight?: boolean;

    // fromTest?: boolean; // ONLY for testing!

    // setActions?: (items: ObjectActionItem[]) => void;
    // resetActions?: () => void;
    // actions?: ObjectActionItem[];
    // setViewOptionState?: (state: ViewOptionStateEnum) => void;
    // noOptionsSideSheet?: boolean;

    
};

export type ObjectManualEntryType = {
    allowChangeDate?: () => Promise<boolean>;
};






function ObjectManualEntryView({
    hideAppBar,
    hideOptions,
    date
}: ObjectManualEntryViewProps, ref: Ref<unknown> | undefined) {

    const [appSettings,] = useAppSettings();
    const [viewOptionState, setViewOptionState] = useState(ViewOptionStateEnum.Default);
    const [newValues, setNewValues] = useState<ITagValue[]>([]);
    const {t} = useTranslation();
    const [offlineState] = useOfflineDetectContext();    
    const [userState] = useUserContext();
    const [,setAppNotificationState] = useAppNotificationContext(); 
    const theme = useTheme();
    const { confirm, acknowledge } = useConfirmation();
    const objectTagValues = useRef<ObjectTagValuesType>(undefined);
    
    const isPhone = isMobile && !isTablet;
    
    let buttonRef = useRef<HTMLButtonElement>(null);
    
    const hasNewValues = newValues.length > 0;



    useImperativeHandle<unknown, ObjectManualEntryType>(
        ref,
        () => ({
            allowChangeDate: canChangeDate
        })
    );




    const canChangeDate = async () => {

        if (newValues.length) {
            return await getUserConfirmation();
        }

        return true;
    }

    const onValueChanged = (tagValue: ITagValue): void => {

        //lodash works
        // let values = [...newValues];
        // _.remove(values, (o) => o.tagID === tagValue.tagID && o.timestamp === tagValue.timestamp);
        // values.push(tagValue);

        // prevent duplicates
        let values = [...newValues];
        let existing = values.find(o => o.tagID === tagValue.tagID && o.timestamp === tagValue.timestamp);
        if (existing) {
             const ndx = values.indexOf(existing);
             if (ndx !== -1) {
                 values.splice(ndx, 1);
             }
        }

        if (tagValue.value !== undefined && isNaN(tagValue.value)) {
            setNewValues(values);
    
        } else {
            values.push(tagValue);
            setNewValues(values);
        }
        //console.log(JSON.stringify(values));
    }

    const onSave = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>): Promise<void> => {

        try {

            buttonRef.current?.setAttribute('disabled', 'disabled');

            if (newValues.length) {
                //console.log('saving values')
                await TagValueDataService.saveTagValues(userState.customer?.id as number,
                    newValues, 
                    true,
                    offlineState.offline);

                setNewValues([]);

                // unfortunately, it can take time for values changes to propagate so
                // refetch after a second
                setTimeout(() => {
                    //console.log('refetching values')
                    objectTagValues.current?.executeAction(BaseActionEnum.Refresh);
                }, 1000);

                acknowledge(offlineState.offline ? 
                    t('tag.offlineValuesStored') : 
                     t('tag.valuesSaved'));
            }
        } catch(error) {

            const err = error as RestError;
                        
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                message: err.message,
                show: true,
                error: err
            });
        }
    }

    const getUserConfirmation = async () => {

        const response = await confirm({
            dialogTitle: t('cancelConfirmation.title'),
            confirmationText: t('tag.cancelEditText'),
            yesButtonText: t('cancelConfirmation.yesButtonText'),
            noButtonText: t('cancelConfirmation.noButtonText'),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        });
        
        if (response === "yes") {
            setNewValues([]);
            return true
        }
        
        return false;
    };

    return (
        <ContentView>
            {!hideAppBar && 
                <ContextualAppBar 
                    viewOptionState={viewOptionState}
                />
            }
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: theme.spacing(1),
                    marginTop: isPhone ? theme.spacing(1) : undefined,
                    position: 'relative',
                    flex: '1 1 auto',
                    overflow: "hidden",
                    height: "100%"
                }}>
                <ObjectTagValues visible={true} 
                    mode={ObjectTagValuesMode.ManualEntry}
                    grouped={true}
                    onValueChanged={onValueChanged}
                    setViewOptionState={setViewOptionState}
                    canChangeDate={canChangeDate}
                    ref={objectTagValues}
                    noOptionsSideSheet={hideOptions}
                    selectedDate={date}
                />

                <UnsavedChangesPrompt
                    hasChanges={hasNewValues}
                    getUserConfirmation={getUserConfirmation}
                />

                <Fab color="secondary"
                    id='save'
                    name='save'
                    type='button'
                    ref={buttonRef}
                    disabled={newValues.length === 0}
                    aria-label="save changes" 
                    sx={{
                        position: 'absolute',
                        bottom: theme.spacing(isPhone ? 2 : 5),
                        //right: theme.spacing(3)
                        right: !hideOptions &&  !isPhone && appSettings.sideSheetOpen ? '340px' : theme.spacing(4)
                    }}
                    onClick={onSave}>
                    <SaveIcon />
                </Fab>

            </Box>
        </ContentView>
    );
}

export default forwardRef(ObjectManualEntryView);
